import { SearchOutlined } from "@ant-design/icons";
import { Input } from "antd";
import { tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { useTable } from "components/UI/Table/hooks/useTable.ts";
import { useTableUrlParameters } from "components/UI/Table/hooks/useTableUrlParameters.ts";
import { useDebouncedValue } from "hooks/useDebouncedValue.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useEffect, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import { SearchFilterEnum } from "types/enums";
import { NonIdentifiable, TableId } from "types/models.ts";

interface QuickSearchProps {
    tableId: TableId;
    dataIndex: string;
    operator?: SearchFilterEnum;
    minLength?: number;
    debounceMs?: number;
    placeholder?: string;
}

export const QuickSearch = ({
    tableId,
    dataIndex,
    // STRING_OPERATORS (constants/filter.ts) only lists MultiIlike for "contains" — plain
    // Ilike isn't one of the string filter type's operators, so it wouldn't match a select option.
    operator = SearchFilterEnum.MultiIlike,
    minLength = 2,
    debounceMs = 500,
    placeholder,
}: QuickSearchProps) => {
    const { t: tC } = usePaldockTranslation("common");
    const tableUtils = useTable(tableId);
    const tableUrlParametersUtils = useTableUrlParameters(tableId);
    const tableState = useRecoilValue(tableAtom<NonIdentifiable>()(tableId));

    // Source of truth for anything applied outside this input (Filter dropdown Apply/Clear).
    const appliedValue = (() => {
        const raw = tableState.filter?.filter?.[dataIndex]?.[operator];
        return typeof raw === "string" ? raw : "";
    })();

    const [value, setValue] = useState<string>(() => {
        const existingValue = tableUrlParametersUtils.getUrlParameters()?.filter?.filter?.[dataIndex]?.[operator];
        return typeof existingValue === "string" ? existingValue : "";
    });

    const debouncedValue = useDebouncedValue(value, debounceMs);
    const isFirstRun = useRef(true);
    // Tracks the value this component itself last applied, so external changes (Filter
    // dropdown Apply/Clear) can be told apart from this component's own debounced writes.
    const lastAppliedByThisRef = useRef(appliedValue);

    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }

        const nextApplied = debouncedValue.length >= minLength ? debouncedValue : undefined;
        lastAppliedByThisRef.current = nextApplied ?? "";
        tableUtils.saveSearch(dataIndex, operator, nextApplied);
    }, [debouncedValue]);

    useEffect(() => {
        if (appliedValue !== lastAppliedByThisRef.current) {
            lastAppliedByThisRef.current = appliedValue;
            setValue(appliedValue);
        }
    }, [appliedValue]);

    return (
        <Input
            allowClear
            prefix={<SearchOutlined className="text-gray-400" />}
            className="w-60"
            placeholder={placeholder ?? tC("search_min_chars", { count: minLength })}
            value={value}
            onChange={(e) => setValue(e.target.value)}
        />
    );
};
